import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRing, Plus, Send, Trash2, History } from "lucide-react";

import { NotifApi, type NotifChannel, type NotifChannelKind, type NotifLogEntry } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { timeAgo, cn } from "@/lib/utils";

export function NotificationsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["notif-channels"],
    queryFn: () => NotifApi.list(),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <BellRing className="size-5 text-primary" />
            {fa.notif.title}
          </h1>
          <p className="text-sm text-muted-foreground">{fa.notif.subtitle}</p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" />
          {fa.notif.add}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            کانال‌های فعال
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.empty}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>نام</TableHead>
                  <TableHead>نوع</TableHead>
                  <TableHead>وضعیت</TableHead>
                  <TableHead>سطح</TableHead>
                  <TableHead>آخرین ارسال</TableHead>
                  <TableHead className="w-32">اقدامات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((c) => <ChannelRow key={c.id} c={c} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewChannelDialog open={open} onOpenChange={setOpen} onSaved={() => {
        qc.invalidateQueries({ queryKey: ["notif-channels"] });
        qc.invalidateQueries({ queryKey: ["notif-log"] });
        setOpen(false);
      }} />

      <NotificationLogCard />
    </div>
  );
}

function NotificationLogCard() {
  const [status, setStatus] = useState<"all" | "sent" | "failed" | "throttled">("all");
  const [channelId, setChannelId] = useState<string>("all");
  const channels = useQuery({ queryKey: ["notif-channels"], queryFn: () => NotifApi.list() });
  const log = useQuery({
    queryKey: ["notif-log", status, channelId],
    queryFn: () =>
      NotifApi.log({
        status: status === "all" ? undefined : status,
        channelId: channelId === "all" ? undefined : channelId,
        limit: 100,
      }),
    refetchInterval: 20_000,
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-foreground">
            <History className="size-4 text-primary" />
            تاریخچهٔ ارسال
          </span>
          <div className="ms-auto flex items-center gap-2">
            <Select value={channelId} onValueChange={setChannelId}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue placeholder="کانال" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">همهٔ کانال‌ها</SelectItem>
                {(channels.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="h-9 w-36">
                <SelectValue placeholder="وضعیت" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">همه</SelectItem>
                <SelectItem value="sent">sent</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
                <SelectItem value="throttled">throttled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {log.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : (log.data?.items ?? []).length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">{fa.common.empty}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>زمان</TableHead>
                <TableHead>کانال</TableHead>
                <TableHead>رویداد</TableHead>
                <TableHead>سطح</TableHead>
                <TableHead>عنوان</TableHead>
                <TableHead>وضعیت</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.data!.items.map((r) => <LogRow key={r.id} r={r} />)}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function LogRow({ r }: { r: NotifLogEntry }) {
  const variant: "secondary" | "destructive" | "outline" =
    r.status === "failed" ? "destructive" : r.status === "sent" ? "secondary" : "outline";
  return (
    <TableRow>
      <TableCell className="text-xs text-muted-foreground">{timeAgo(r.sentAt)}</TableCell>
      <TableCell>{r.channelName}</TableCell>
      <TableCell className="text-xs lat-num">{r.event}</TableCell>
      <TableCell className="text-xs">{r.payload.level ?? "—"}</TableCell>
      <TableCell className="text-xs max-w-[28rem] truncate" title={r.payload.title ?? ""}>
        {r.payload.title ?? "—"}
      </TableCell>
      <TableCell>
        <Badge variant={variant} className="lat-num" title={r.errorMsg ?? undefined}>
          {r.status}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

function ChannelRow({ c }: { c: NotifChannel }) {
  const qc = useQueryClient();
  const test = useMutation({
    mutationFn: () => NotifApi.test(c.id),
    onSuccess: () => toast.success("پیام تست ارسال شد"),
    onError: (e: Error) => toast.error("ارسال ناموفق", { description: e.message }),
  });
  const remove = useMutation({
    mutationFn: () => NotifApi.remove(c.id),
    onSuccess: () => { toast.success("حذف شد"); qc.invalidateQueries({ queryKey: ["notif-channels"] }); },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => NotifApi.update(c.id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-channels"] }),
  });

  return (
    <TableRow>
      <TableCell className="font-medium">{c.name}</TableCell>
      <TableCell><Badge variant="outline" className="lat-num">{c.kind}</Badge></TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch checked={c.enabled} onCheckedChange={(v) => toggle.mutate(v)} />
          <span className="text-xs text-muted-foreground">{c.enabled ? "فعال" : "غیرفعال"}</span>
        </div>
      </TableCell>
      <TableCell className="text-xs">{c.minLevel}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {c.lastSentAt ? timeAgo(c.lastSentAt) : "—"}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="sm" title={fa.notif.test} onClick={() => test.mutate()}>
            <Send className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={() => { if (confirm("کانال حذف شود؟")) remove.mutate(); }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function NewChannelDialog({
  open, onOpenChange, onSaved,
}: { open: boolean; onOpenChange: (b: boolean) => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<NotifChannelKind>("telegram");
  const [minLevel, setMinLevel] = useState<"info" | "warn" | "error">("warn");
  // Per-kind config
  const [telegramToken, setTelegramToken] = useState("");
  const [telegramChat, setTelegramChat] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [smtpTo, setSmtpTo] = useState("");
  const [smsApiKey, setSmsApiKey] = useState("");
  const [smsTemplateId, setSmsTemplateId] = useState("");
  const [smsMobiles, setSmsMobiles] = useState("");
  const [smsBindingsJson, setSmsBindingsJson] = useState("");

  const create = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = {};
      if (kind === "telegram") Object.assign(config, { botToken: telegramToken, chatId: telegramChat });
      if (kind === "webhook") Object.assign(config, { url: webhookUrl });
      if (kind === "smtp") Object.assign(config, {
        host: smtpHost, port: smtpPort, user: smtpUser, pass: smtpPass, from: smtpFrom, to: smtpTo,
      });
      if (kind === "sms") {
        const tid = Number(String(smsTemplateId).trim());
        if (!Number.isFinite(tid) || tid <= 0) throw new Error("شناسهٔ قالب (templateId) نامعتبر است");
        let bindings: unknown[] | undefined;
        if (smsBindingsJson.trim()) {
          try {
            const parsed = JSON.parse(smsBindingsJson) as unknown;
            if (!Array.isArray(parsed)) throw new Error("باید آرایهٔ JSON باشد");
            bindings = parsed;
          } catch {
            throw new Error("فرمت JSON نگاشت پارامترها نامعتبر است");
          }
        }
        Object.assign(config, {
          apiKey: smsApiKey,
          templateId: tid,
          receptors: smsMobiles,
          ...(bindings ? { bindings } : {}),
        });
      }
      return NotifApi.create({ name, kind, minLevel, config });
    },
    onSuccess: () => { toast.success("کانال اضافه شد"); onSaved(); },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{fa.notif.add}</DialogTitle>
          <DialogDescription>
            رویدادهای مهم (شکست بکاپ، رد شدن از حد دیسک، ...) به این کانال‌ها ارسال می‌شوند.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <div className="grid gap-2">
            <Label>نام</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>نوع</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as NotifChannelKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                  <SelectItem value="smtp">SMTP (Email)</SelectItem>
                  <SelectItem value="sms">SMS.ir (الگویی)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>حداقل سطح</Label>
              <Select value={minLevel} onValueChange={(v) => setMinLevel(v as never)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">info</SelectItem>
                  <SelectItem value="warn">warn</SelectItem>
                  <SelectItem value="error">error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {kind === "telegram" && (
            <>
              <div className="grid gap-2">
                <Label>Bot Token</Label>
                <Input dir="ltr" value={telegramToken} onChange={(e) => setTelegramToken(e.target.value)} required />
              </div>
              <div className="grid gap-2">
                <Label>Chat ID</Label>
                <Input dir="ltr" value={telegramChat} onChange={(e) => setTelegramChat(e.target.value)} required />
              </div>
            </>
          )}
          {kind === "webhook" && (
            <div className="grid gap-2">
              <Label>Webhook URL</Label>
              <Input dir="ltr" type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} required />
            </div>
          )}
          {kind === "smtp" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>SMTP Host</Label>
                <Input dir="ltr" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} required />
              </div>
              <div className="grid gap-2">
                <Label>Port</Label>
                <Input dir="ltr" type="number" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value))} />
              </div>
              <div className="grid gap-2">
                <Label>User</Label>
                <Input dir="ltr" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>Password</Label>
                <Input dir="ltr" type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} />
              </div>
              <div className="grid gap-2 col-span-2">
                <Label>From</Label>
                <Input dir="ltr" value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} required />
              </div>
              <div className="grid gap-2 col-span-2">
                <Label>To (comma-separated)</Label>
                <Input dir="ltr" value={smtpTo} onChange={(e) => setSmtpTo(e.target.value)} required />
              </div>
            </div>
          )}
          {kind === "sms" && (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>کلید وب‌سرویس (SMS.ir)</Label>
                <Input dir="ltr" type="password" value={smsApiKey} onChange={(e) => setSmsApiKey(e.target.value)} required />
              </div>
              <div className="grid gap-2">
                <Label>شناسه قالب (templateId از پنل)</Label>
                <Input dir="ltr" inputMode="numeric" value={smsTemplateId} onChange={(e) => setSmsTemplateId(e.target.value)} required />
              </div>
              <div className="grid gap-2">
                <Label>گیرندگان (جداشده با کاما)</Label>
                <Input dir="ltr" value={smsMobiles} onChange={(e) => setSmsMobiles(e.target.value)} required placeholder="0912…,0915…" />
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">نگاشت پارامترهای قالب (JSON، اختیاری)</Label>
                <textarea
                  dir="ltr"
                  className={cn(
                    "min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs shadow-sm transition-colors",
                    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                  placeholder='[{"name":"TITLE","from":"title","maxLen":120},{"name":"BODY","from":"message","maxLen":300}]'
                  value={smsBindingsJson}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSmsBindingsJson(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  نام فیلد <span className="font-mono">name</span> باید دقیقاً همان پارامتر قالب تأییدشده در sms.ir باشد (بدون #). مقادیر
                  <span className="font-mono">from</span> مجاز:
                  {" "}
                  <span className="font-mono">level</span>
                  ،
                  {" "}
                  <span className="font-mono">title</span>
                  ،
                  {" "}
                  <span className="font-mono">message</span>
                  ،
                  {" "}
                  <span className="font-mono">event</span>. اگر خالی بماند پیش‌فرض LEVEL/TITLE/BODY/EVENT استفاده می‌شود؛ معمولاً باید با قالب خودتان در پنل یکسان شود.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>{fa.common.save}</Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>{fa.common.cancel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
